/**
 * Lift-delta computation for the Arm Comparison table.
 *
 * ### Why this doesn't use the wire's `test_*_lift`
 *
 * `arm-comparison` sends `test_a_lift` / `test_b_lift` as a **relative decimal
 * fraction** — `(variant - control) / control` — regardless of the metric's own
 * unit. The previous implementation formatted that fraction *using the metric's
 * unit*, which produced one wrong number per unit from a single mistake:
 *
 *   revenue_per_product  0.3114 → "+$0.31"  (fraction printed as dollars)
 *   roas                 0.5090 → "+0.51x"  (fraction printed as an x-multiple)
 *   impressions          -0.2178 → "-0"     (fraction rounded to an integer)
 *   ctr / conv_rate      0.3557 → "+35.57%" (accidentally correct: ×100 is right)
 *
 * Deltas are now derived from the raw `control` / `test_*` values instead. Two
 * reasons: the absolute x-delta wanted for ROAS is not recoverable from a
 * relative fraction, and deriving from the same numbers the table prints keeps
 * each badge arithmetically consistent with the two values beside it. The wire
 * lift agrees with this computation to full float precision, so nothing is lost.
 */

import type { AnalyticsArmComparisonRow, AnalyticsMetricUnit } from '../types/analytics.types';
import type { ArmSlot } from '../types/experiment.types';

const EM_DASH = '—';

/** Rate / currency / count metrics render a relative lift, to 1 dp. */
const PERCENT_DECIMALS = 1;
/** ROAS renders an absolute x-delta, to 2 dp. */
const RATIO_DECIMALS = 2;

/**
 * Formats one arm's lift against Control. Percent lift for rate, currency and
 * count metrics; absolute x-delta for ROAS, where a relative lift on a value
 * that is already a ratio reads as a confusing double-relative number.
 *
 * Returns an em dash when either value is missing, when Control is zero (no
 * meaningful relative lift, and a divide-by-zero), or when the delta rounds to
 * zero — "+0.0%" and "-0" imply a signal the data doesn't support.
 */
export function formatArmLift(
  control: number | null | undefined,
  variant: number | null | undefined,
  unit: AnalyticsMetricUnit,
): string {
  if (control == null || variant == null) return EM_DASH;

  if (unit === 'ratio') return formatSignedDelta(variant - control, RATIO_DECIMALS, 'x');

  if (control === 0) return EM_DASH;
  return formatSignedDelta(((variant - control) / control) * 100, PERCENT_DECIMALS, '%');
}

/** Signs and rounds a delta, collapsing anything that rounds to zero to an em dash. */
function formatSignedDelta(value: number, decimals: number, suffix: string): string {
  if (!Number.isFinite(value)) return EM_DASH;
  // Round before the zero check: -0.04% must read as "no measurable change" at
  // this precision, not "-0.0%".
  const rounded = Number(value.toFixed(decimals));
  if (rounded === 0) return EM_DASH;
  const sign = rounded > 0 ? '+' : '-';
  return `${sign}${Math.abs(rounded).toFixed(decimals)}${suffix}`;
}

/**
 * Which arm holds the highest value in a row — drives the "best value" accent
 * dot. Every metric on this table (CTR, conversion rate, revenue per product,
 * ROAS, impressions per product) is higher-is-better, so this is a plain max;
 * a future lower-is-better metric (e.g. CPC) would need a direction per row.
 *
 * Ties resolve leftmost (Control, then Arm B) so exactly one dot renders.
 * `null` when the row has no values at all.
 */
export function resolveBestArm(row: AnalyticsArmComparisonRow): ArmSlot | null {
  const candidates: { slot: ArmSlot; value: number | null }[] = [
    { slot: 'control', value: row.control },
    { slot: 'testA', value: row.test_a },
    { slot: 'testB', value: row.test_b },
  ];

  let best: { slot: ArmSlot; value: number } | null = null;
  for (const candidate of candidates) {
    if (candidate.value == null || !Number.isFinite(candidate.value)) continue;
    if (best === null || candidate.value > best.value) {
      best = { slot: candidate.slot, value: candidate.value };
    }
  }
  return best?.slot ?? null;
}
